import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@hooks/usePermissions';
import { useAuth } from '@hooks/useAuth';
import { USER_ROLES } from '@constants';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const PermissionGuard = ({ 
  children, 
  requiredPermission, 
  requiredPermissions = [], 
  requireAll = false,
  fallbackPath = '/admin/dashboard',
  adminOnly = false 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading, isSuperAdmin } = usePermissions();

  useEffect(() => {
    // Don't check permissions while still loading
    if (loading) return;

    // Check admin role if required
    if (adminOnly && user?.role !== USER_ROLES.ADMIN) {
      toast.error('Access denied. Admin only.');
      navigate(fallbackPath);
      return;
    }

    // Super admins have all permissions
    if (isSuperAdmin) return;

    // Check single permission
    if (requiredPermission && !hasPermission(requiredPermission)) {
      toast.error('You do not have permission to access this page');
      navigate(fallbackPath);
      return;
    }

    // Check multiple permissions
    if (requiredPermissions.length > 0) {
      const hasAccess = requireAll 
        ? hasAllPermissions(requiredPermissions)
        : hasAnyPermission(requiredPermissions);
      
      if (!hasAccess) {
        toast.error('You do not have permission to access this page');
        navigate(fallbackPath);
        return;
      }
    }
  }, [
    loading, 
    user?.role, 
    isSuperAdmin, 
    requiredPermission, 
    requiredPermissions, 
    requireAll, 
    adminOnly,
    fallbackPath,
    navigate,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  ]);

  // Show loading spinner while checking permissions
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return children;
};

export default PermissionGuard;
